import { Link } from "react-router-dom";
import { MapPin, Phone, MessageCircle, Clock, Star, Instagram } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { Reveal, GoldLine } from "@/components/Reveal";
import {
    GUIDES, INSTAGRAM_IMAGES, INSTAGRAM_URL, ADDRESS, PHONE_DISPLAY, PHONE_TEL,
    HOURS, MAP_EMBED, DIRECTIONS_URL, waLink, WA_MESSAGES, BRAND_FULL,
} from "@/config/site";
import { openBooking } from "@/lib/api";

export const Knowledge = () => (
    <section className="py-24 sm:py-36 bg-charcoal/30" data-testid="knowledge-preview">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8">
            <SectionHeading
                eyebrow="The BSK Jewellery Guide"
                title={<>Knowledge, <span className="italic gold-text">Beautifully Shared</span></>}
                copy="Understand gold purity, diamonds and care — before you buy."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {GUIDES.slice(0, 4).map((g, i) => (
                    <Reveal key={g.slug} delay={i * 0.08}>
                        <Link to={`/guide/${g.slug}`} data-testid={`guide-card-${g.slug}`} className="group block border border-gold/10 hover:border-gold/50 transition-colors duration-500 bg-obsidian">
                            <div className="aspect-[16/10] overflow-hidden">
                                <img src={g.image} alt={g.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                            </div>
                            <div className="p-6">
                                <p className="text-[10px] tracking-[0.25em] uppercase text-gold">{g.readTime} read</p>
                                <h3 className="font-serif text-xl text-ivory mt-2 group-hover:text-gold transition-colors">{g.title}</h3>
                                <p className="text-xs text-sand mt-2 leading-relaxed">{g.excerpt}</p>
                            </div>
                        </Link>
                    </Reveal>
                ))}
            </div>
            <Reveal className="text-center mt-10">
                <Link to="/guide" data-testid="knowledge-view-all-btn" className="text-[11px] tracking-[0.25em] uppercase text-gold border-b border-gold/50 pb-1 hover:border-gold transition-colors">
                    View All Guides →
                </Link>
            </Reveal>
        </div>
    </section>
);

export const Reviews = () => (
    <section className="py-24 sm:py-32" data-testid="customer-reviews">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 text-center">
            <SectionHeading
                eyebrow="Loved by Our Customers"
                title={<>Every Story Begins in <span className="italic gold-text">Our Showroom</span></>}
            />
            <Reveal>
                <div className="border border-gold/20 bg-charcoal/50 p-10 sm:p-14">
                    <p className="font-serif text-6xl sm:text-7xl gold-text">4.5</p>
                    <div className="flex justify-center gap-1.5 text-gold mt-4" aria-label="4.5 out of 5 stars on Google">
                        {[...Array(5)].map((_, i) => (
                            <Star key={i} size={18} strokeWidth={1.25} fill="currentColor" />
                        ))}
                    </div>
                    <p className="font-serif italic text-xl sm:text-2xl text-ivory/90 leading-relaxed mt-7 max-w-xl mx-auto">
                        "Rated 4.5 out of 5 from 292 reviews on our Google Business profile —
                        trust earned one family at a time, in Rani Ka Bagh, Amritsar."
                    </p>
                    <GoldLine className="w-16 mx-auto my-7" />
                    <p className="text-[11px] tracking-[0.25em] uppercase text-sand">BSK Jewellers · Bhim Sen Khanna Chandiwale · Amritsar</p>
                    <a
                        href={DIRECTIONS_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="reviews-see-more-btn"
                        className="inline-block mt-8 text-[11px] tracking-[0.25em] uppercase text-gold border-b border-gold/50 pb-1 hover:border-gold transition-colors"
                    >
                        See More Reviews →
                    </a>
                </div>
            </Reveal>
        </div>
    </section>
);

export const InstagramSection = () => (
    <section className="py-24 sm:py-36 overflow-hidden" data-testid="instagram-section">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8">
            <SectionHeading
                eyebrow="Instagram"
                title={<>From Our World <span className="italic gold-text">to Yours</span></>}
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {INSTAGRAM_IMAGES.map((src, i) => (
                    <Reveal key={src} delay={i * 0.05}>
                        <a
                            href={INSTAGRAM_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`instagram-tile-${i}`}
                            className="group relative block aspect-square overflow-hidden border border-gold/10 hover:border-gold/50 transition-colors"
                        >
                            <img src={src} alt={`BSK Jewellers jewellery editorial ${i + 1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                            <div className="absolute inset-0 bg-obsidian/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                                <Instagram size={22} className="text-gold" strokeWidth={1.5} />
                            </div>
                        </a>
                    </Reveal>
                ))}
            </div>
            <Reveal className="text-center mt-10">
                <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" data-testid="instagram-follow-btn" className="text-[11px] tracking-[0.25em] uppercase text-gold border-b border-gold/50 pb-1 hover:border-gold transition-colors">
                    Follow BSK Jewellers →
                </a>
            </Reveal>
        </div>
    </section>
);

export const VisitShowroom = () => (
    <section className="py-24 sm:py-36 bg-charcoal/30 border-y border-gold/10" data-testid="visit-showroom">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
                <Reveal>
                    <span className="eyebrow">Visit BSK Jewellers in Rani Ka Bagh, Amritsar</span>
                </Reveal>
                <Reveal delay={0.08}>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.12] mt-5">
                        Find Us in <span className="italic gold-text">Amritsar</span>
                    </h2>
                </Reveal>
                <GoldLine delay={0.15} className="w-20 my-7" />
                <Reveal delay={0.2}>
                    <p className="font-serif text-xl text-ivory">{BRAND_FULL}</p>
                    <div className="mt-6 space-y-4 text-sm text-sand">
                        <p className="flex items-start gap-3"><MapPin size={16} className="text-gold mt-0.5 shrink-0" /> {ADDRESS}</p>
                        <p className="flex items-center gap-3"><Phone size={16} className="text-gold shrink-0" /> <a href={`tel:${PHONE_TEL}`} className="hover:text-gold transition-colors" data-testid="visit-phone">{PHONE_DISPLAY}</a></p>
                        <p className="flex items-center gap-3"><MessageCircle size={16} className="text-gold shrink-0" /> <a href={waLink(WA_MESSAGES.general)} target="_blank" rel="noreferrer" className="hover:text-gold transition-colors" data-testid="visit-whatsapp">WhatsApp {PHONE_DISPLAY}</a></p>
                        <p className="flex items-center gap-3"><Clock size={16} className="text-gold shrink-0" /> {HOURS}</p>
                    </div>
                </Reveal>
                <Reveal delay={0.3}>
                    <div className="flex flex-wrap gap-4 mt-9">
                        <a href={DIRECTIONS_URL} target="_blank" rel="noreferrer" data-testid="visit-directions-btn" className="bg-gold text-obsidian text-[11px] tracking-[0.25em] uppercase px-8 py-4 hover:bg-gold-bright transition-colors">
                            Get Directions →
                        </a>
                        <button onClick={() => openBooking()} data-testid="visit-book-btn" className="border border-gold/50 text-gold text-[11px] tracking-[0.25em] uppercase px-8 py-4 hover:bg-gold hover:text-obsidian transition-colors">
                            Book a Visit
                        </button>
                    </div>
                </Reveal>
            </div>
            <Reveal delay={0.15}>
                <div className="relative border border-gold/25 p-2 bg-obsidian" data-testid="map-card">
                    <div className="absolute -top-px -left-px w-8 h-8 border-t-2 border-l-2 border-gold" />
                    <div className="absolute -bottom-px -right-px w-8 h-8 border-b-2 border-r-2 border-gold" />
                    <iframe
                        title="BSK Jewellers — Rani Ka Bagh, Amritsar on Google Maps"
                        src={MAP_EMBED}
                        className="w-full aspect-[4/3] grayscale invert-[0.92] contrast-[0.9] opacity-90"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        data-testid="google-map-embed"
                    />
                </div>
            </Reveal>
        </div>
    </section>
);

export const ConsultationCTA = () => (
    <section className="relative py-28 sm:py-40 overflow-hidden grain" data-testid="consultation-cta">
        <img src="/images/earrings-pearl.jpg" alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/70 to-obsidian" />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
            <Reveal>
                <span className="eyebrow">Private Consultation</span>
            </Reveal>
            <Reveal delay={0.1}>
                <h2 className="font-serif text-3xl sm:text-5xl leading-[1.1] mt-6">
                    Make Your Jewellery Experience <span className="italic gold-text">Personal</span>
                </h2>
            </Reveal>
            <Reveal delay={0.2}>
                <p className="text-sand text-sm sm:text-base mt-6 max-w-lg mx-auto leading-relaxed">
                    Visit BSK Jewellers for a personalised conversation about your jewellery requirements.
                </p>
            </Reveal>
            <Reveal delay={0.3}>
                <button
                    onClick={() => openBooking()}
                    data-testid="cta-request-appointment-btn"
                    className="mt-10 bg-gold text-obsidian text-[11px] tracking-[0.25em] uppercase px-10 py-4 hover:bg-gold-bright transition-colors"
                >
                    Request Appointment
                </button>
            </Reveal>
        </div>
    </section>
);
